import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { useOrg } from '../../../contexts/OrgContext'
import { usePollingInterval } from '../../../hooks/usePollingInterval'

const TABS = [
  { id: 'all', label: 'All Logs' },
  { id: 'change', label: 'Changes' },
  { id: 'login', label: 'Logins' },
  { id: 'system', label: 'System' }
]

const CATEGORY_COLORS = {
  change: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  login: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  system: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
}

const ACTION_ICONS = {
  login: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
    </svg>
  ),
  change: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  system: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

const PAGE_SIZE = 50

function LoginMetadataPanel({ metadata }) {
  const m = metadata || {}
  const details = [
    { label: 'Browser', value: m.browser },
    { label: 'Operating System', value: m.os },
    { label: 'Platform', value: m.platform },
    { label: 'Language', value: m.language },
    { label: 'Timezone', value: m.timezone },
    { label: 'Screen', value: m.screen ? `${m.screen.width} × ${m.screen.height}` : null },
    { label: 'Viewport', value: m.viewport ? `${m.viewport.width} × ${m.viewport.height}` : null },
    { label: 'Pixel Ratio', value: m.devicePixelRatio ? `${m.devicePixelRatio}x` : null },
    { label: 'Cookies Enabled', value: m.cookiesEnabled != null ? (m.cookiesEnabled ? 'Yes' : 'No') : null },
    { label: 'Online', value: m.online != null ? (m.online ? 'Yes' : 'No') : null },
    { label: 'Network Type', value: m.network?.effectiveType },
    { label: 'Downlink', value: m.network?.downlink != null ? `${m.network.downlink} Mbps` : null },
    { label: 'Latency (RTT)', value: m.network?.rtt != null ? `${m.network.rtt} ms` : null }
  ].filter(d => d.value)

  return (
    <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-700/50 rounded-lg" onClick={e => e.stopPropagation()}>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Session Details</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
        {details.map(d => (
          <div key={d.label} className="flex items-baseline gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{d.label}:</span>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{d.value}</span>
          </div>
        ))}
      </div>
      {m.userAgent && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">User Agent</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 font-mono break-all leading-relaxed">{m.userAgent}</p>
        </div>
      )}
    </div>
  )
}

function AuditLogs() {
  const { clients: orgClients } = useAssessment()
  const { currentOrg } = useOrg()
  const [orgMemberIds, setOrgMemberIds] = useState([])
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [expandedRow, setExpandedRow] = useState(null)
  const [selectedLog, setSelectedLog] = useState(null)
  const [panelTop, setPanelTop] = useState(65)

  // Find the actual header bottom
  useEffect(() => {
    if (!selectedLog) return
    const update = () => {
      const header = document.querySelector('[data-main-header]')
      if (header) {
        setPanelTop(header.getBoundingClientRect().bottom)
      }
    }
    update()
    const timer = setTimeout(update, 100)
    return () => clearTimeout(timer)
  }, [selectedLog])

  // Client list scoped to current org (from AssessmentContext)
  const clients = useMemo(() =>
    orgClients.map(c => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [orgClients]
  )
  const orgClientIds = useMemo(() => orgClients.map(c => c.id), [orgClients])

  // Fetch org member user IDs for user-scoped events (login, MFA)
  // Exclude global_admin users — their events belong in the Global Admin Activity Log only
  useEffect(() => {
    if (!currentOrg?.id) return
    supabase
      .from('org_members')
      .select('user_id, profiles!org_members_user_id_profiles_fk(role)')
      .eq('org_id', currentOrg.id)
      .then(({ data }) => {
        const nonGlobal = (data || []).filter(m => m.profiles?.role !== 'global_admin')
        setOrgMemberIds(nonGlobal.map(m => m.user_id))
      })
  }, [currentOrg?.id])

  const [lastRefreshed, setLastRefreshed] = useState(null)

  const fetchLogs = useCallback(async () => {
    if (orgClientIds.length === 0 && orgMemberIds.length === 0) {
      setLogs([])
      setTotalCount(0)
      setHasMore(false)
      setLoading(false)
      return
    }

    setLoading(true)
    const since = new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString()

    // Two separate queries to ensure strict org isolation:
    // 1. Client-scoped events (changes, assessments, policies) for this org's clients
    // 2. User-scoped events (login, MFA) for this org's members with no client_id (org-level events only)
    const [clientEventsRes, userEventsRes] = await Promise.all([
      orgClientIds.length > 0
        ? supabase
            .from('audit_logs')
            .select('*')
            .in('client_id', orgClientIds)
            .gte('created_at', since)
            .order('created_at', { ascending: false })
            .limit(PAGE_SIZE * 2)
        : Promise.resolve({ data: [] }),
      orgMemberIds.length > 0
        ? supabase
            .from('audit_logs')
            .select('*')
            .in('user_id', orgMemberIds)
            .is('client_id', null)
            .gte('created_at', since)
            .order('created_at', { ascending: false })
            .limit(PAGE_SIZE)
        : Promise.resolve({ data: [] })
    ])

    // Merge + deduplicate + sort
    const seen = new Set()
    const allEvents = [...(clientEventsRes.data || []), ...(userEventsRes.data || [])]
      .filter(e => { if (seen.has(e.id)) return false; seen.add(e.id); return true })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    // Apply client-side filters
    let results = allEvents

    if (activeTab !== 'all') {
      results = results.filter(e => e.category === activeTab)
    }
    if (clientFilter) {
      results = results.filter(e => e.client_id === clientFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      results = results.filter(e =>
        (e.description || '').toLowerCase().includes(q) ||
        (e.action || '').toLowerCase().includes(q) ||
        (e.user_email || '').toLowerCase().includes(q)
      )
    }

    // Paginate
    const total = results.length
    const paged = results.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    setLogs(paged)
    setTotalCount(total)
    setHasMore((page + 1) * PAGE_SIZE < total)
    setLastRefreshed(new Date())
    setLoading(false)
  }, [activeTab, clientFilter, searchQuery, page, orgClientIds, orgMemberIds])

  // 5-minute polling
  usePollingInterval(fetchLogs, 5 * 60 * 1000, orgClientIds.length > 0)

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [activeTab, clientFilter, searchQuery])

  const formatDate = (dateStr) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    })
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Logs</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Last 72 hours · auto-refreshes every 5 min
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Updated {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchLogs}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">All Clients</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {totalCount} event{totalCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Log table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">No audit logs found</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Events will appear here as actions are performed.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Timestamp</th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Category</th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">User</th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Action</th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Description</th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Client</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {logs.map(log => {
                const isLogin = log.category === 'login'
                const hasMetadata = isLogin && log.metadata && Object.keys(log.metadata).length > 0
                const isExpanded = expandedRow === log.id
                return (
                  <tr
                    key={log.id}
                    className={`transition-colors cursor-pointer ${selectedLog?.id === log.id ? 'bg-primary-50 dark:bg-primary-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
                    onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                  >
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap align-top">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full ${CATEGORY_COLORS[log.category] || CATEGORY_COLORS.system}`}>
                        {ACTION_ICONS[log.category] || ACTION_ICONS.system}
                        {log.category.charAt(0).toUpperCase() + log.category.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-xs font-medium text-primary-700 dark:text-primary-300">
                          {(log.user_email || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm text-gray-900 dark:text-white truncate max-w-[180px]">{log.user_email || 'System'}</p>
                          {log.user_role && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">{log.user_role}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <code className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
                        {log.action}
                      </code>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate">
                        {log.description}
                      </div>
                      {hasMetadata && (
                        <div className="flex items-center gap-1 mt-1">
                          <svg className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {isExpanded ? 'Hide' : 'View'} session details
                          </span>
                        </div>
                      )}
                      {isExpanded && hasMetadata && (
                        <LoginMetadataPanel metadata={log.metadata} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap align-top">
                      {log.client_name || '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail side panel */}
      {selectedLog && (
        <div className="fixed right-0 z-20 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl" style={{ top: `${panelTop}px`, height: `calc(100vh - ${panelTop}px)` }}>
          {/* Panel header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-4 flex items-center justify-between z-10">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Event Details</h3>
            <button
              onClick={() => setSelectedLog(null)}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-5 py-4 space-y-5 overflow-y-auto" style={{ maxHeight: `calc(100vh - ${panelTop + 57}px)` }}>
            {/* Category + Action */}
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${CATEGORY_COLORS[selectedLog.category] || CATEGORY_COLORS.system}`}>
                {ACTION_ICONS[selectedLog.category] || ACTION_ICONS.system}
                {selectedLog.category.charAt(0).toUpperCase() + selectedLog.category.slice(1)}
              </span>
              <code className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                {selectedLog.action}
              </code>
            </div>

            {/* Description */}
            <div>
              <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Description</p>
              <p className="text-sm text-gray-900 dark:text-white leading-relaxed">{selectedLog.description || 'No description'}</p>
            </div>

            {/* Timestamp */}
            <div>
              <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Timestamp</p>
              <p className="text-sm text-gray-900 dark:text-white">
                {new Date(selectedLog.created_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(selectedLog.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
              </p>
            </div>

            {/* User */}
            <div>
              <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">User</p>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-xs font-medium text-primary-700 dark:text-primary-300">
                  {(selectedLog.user_email || '?')[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-sm text-gray-900 dark:text-white">{selectedLog.user_email || 'System'}</p>
                  {selectedLog.user_role && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{selectedLog.user_role.replace(/_/g, ' ')}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Client */}
            {selectedLog.client_name && (
              <div>
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Client</p>
                <p className="text-sm text-gray-900 dark:text-white">{selectedLog.client_name}</p>
              </div>
            )}

            {/* Resource */}
            {(selectedLog.resource_type || selectedLog.resource_id) && (
              <div>
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Resource</p>
                {selectedLog.resource_type && (
                  <p className="text-sm text-gray-900 dark:text-white capitalize">{selectedLog.resource_type.replace(/_/g, ' ')}</p>
                )}
                {selectedLog.resource_id && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-0.5">{selectedLog.resource_id}</p>
                )}
              </div>
            )}

            {/* Event ID */}
            <div>
              <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Event ID</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">{selectedLog.id}</p>
            </div>

            {/* Login Session Details */}
            {selectedLog.category === 'login' && selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Session Details</p>
                <div className="space-y-1.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  {[
                    { label: 'Browser', value: selectedLog.metadata.browser },
                    { label: 'OS', value: selectedLog.metadata.os },
                    { label: 'Platform', value: selectedLog.metadata.platform },
                    { label: 'Language', value: selectedLog.metadata.language },
                    { label: 'Timezone', value: selectedLog.metadata.timezone },
                    { label: 'Screen', value: selectedLog.metadata.screen ? `${selectedLog.metadata.screen.width} x ${selectedLog.metadata.screen.height}` : null },
                    { label: 'Viewport', value: selectedLog.metadata.viewport ? `${selectedLog.metadata.viewport.width} x ${selectedLog.metadata.viewport.height}` : null },
                    { label: 'Network', value: selectedLog.metadata.network?.effectiveType },
                    { label: 'Downlink', value: selectedLog.metadata.network?.downlink != null ? `${selectedLog.metadata.network.downlink} Mbps` : null },
                    { label: 'Latency', value: selectedLog.metadata.network?.rtt != null ? `${selectedLog.metadata.network.rtt} ms` : null }
                  ].filter(d => d.value).map(d => (
                    <div key={d.label} className="flex items-baseline justify-between">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">{d.label}</span>
                      <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* What Changed (before/after diff) */}
            {selectedLog.metadata?.changes && Object.keys(selectedLog.metadata.changes).length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">What Changed</p>
                <div className="space-y-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  {Object.entries(selectedLog.metadata.changes).map(([field, change]) => {
                    const label = field.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim()
                    const formatVal = (v) => {
                      if (v === null || v === undefined || v === '') return 'Empty'
                      if (Array.isArray(v)) return v.join(', ')
                      if (typeof v === 'object') return JSON.stringify(v)
                      return String(v)
                    }
                    return (
                      <div key={field}>
                        <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</p>
                        <div className="flex items-start gap-2 text-[12px]">
                          <span className="text-red-500 dark:text-red-400 line-through break-words flex-1">{formatVal(change.from)}</span>
                          <span className="text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5">→</span>
                          <span className="text-green-600 dark:text-green-400 font-medium break-words flex-1">{formatVal(change.to)}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Additional Information (non-change metadata) */}
            {selectedLog.category !== 'login' && selectedLog.metadata && (() => {
              const skip = new Set(['changes', 'fields'])
              const entries = Object.entries(selectedLog.metadata).filter(([k]) => !skip.has(k))
              if (entries.length === 0) return null
              return (
                <div>
                  <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Additional Information</p>
                  <div className="space-y-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    {entries.map(([key, value]) => {
                      const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                      let displayValue
                      if (Array.isArray(value)) {
                        displayValue = value.join(', ')
                      } else if (typeof value === 'object' && value !== null) {
                        displayValue = Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ')
                      } else if (typeof value === 'boolean') {
                        displayValue = value ? 'Yes' : 'No'
                      } else {
                        displayValue = String(value)
                      }
                      return (
                        <div key={key}>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">{label}</p>
                          <p className="text-[12px] text-gray-800 dark:text-gray-200 break-words">{displayValue}</p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })()}

            {/* Raw Data (JSON) */}
            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Raw Data</p>
                <pre className="text-[11px] text-gray-600 dark:text-gray-400 font-mono bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words leading-relaxed">
                  {JSON.stringify(selectedLog.metadata, null, 2)}
                </pre>
              </div>
            )}

            {/* User Agent (login) */}
            {selectedLog.metadata?.userAgent && (
              <div>
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">User Agent</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono break-all leading-relaxed bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  {selectedLog.metadata.userAgent}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AuditLogs
